import { useState, useMemo, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Dropdown, Modal, Button, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEllipsis } from '@fortawesome/free-solid-svg-icons';
import ResizableBottomPanel from './ResizablePanel';

import {
  cancelNodeInstance,
  holdNodeInstance,
  releaseNodeInstance,
  fetchNodeInstances,
  fetchNodeInstanceCount,
  killNodeInstance,
  disableNodeInstance,
  revertDisableNodeInstance,
  deleteNodeInstance,
  deleteMultipleNodeInstances,
  fetchNodeInstanceById,
} from '../../../../redux/slices/node_instancesSlice';
import { getFolders } from '../../../../redux/slices/lists/foldersSlice';
import { getNodeTypes, isNodeTypeDeprecated } from '../../../../redux/slices/lists/nodeTypesSlice';
import { buildFolderPathOptions } from '../../../../helpers/folderPaths';
import { setNodeInstanceId, setVisibility, clearPanel } from '../../../../redux/slices/panelSlice';
import { canCancel, canHold, canRelease, canKill, canDelete, canUpdate, canDisable, canRevertDisable } from 'utils/constants';
import { canOpenUnblockNodeInstanceDialog, resolveEffectiveStatusForActions } from 'helpers/nodeInstanceStatus';
import { useTableController } from 'hooks/useTableController';
import useAdvanceTable from 'hooks/useAdvanceTable';
import TableViewLayout from 'layouts/TableViewLayout';
import { useAppDispatch, useAppSelector } from 'redux/store';
import ExpandableRow from 'components/tables/ExpandableRow';
import NodeEditorLink from 'components/common/NodeEditorLink';
import { useAppContext } from 'providers/AppProvider';
import { useExpandedRows } from 'hooks/useExpandedRows';
import OriginatingFlowBadge from './OriginatingFlowBadge';
import NoteIndicator from './NoteIndicator';
import { NODE_INSTANCE_NOTES_CHANGED } from '../customer_node_definitions/node_definition_editor/components/ui/NodeInstanceNotes';
import UnblockNodeInstanceDialog from 'components/common/UnblockNodeInstanceDialog';

// Positions the per-row actions menu relative to the viewport instead of the table's own
// scrollable ancestor, so it renders outside that overflow:auto clipping box instead of
// getting cut off when it opens near the bottom of the table's visible area.
const ACTIONS_DROPDOWN_POPPER_CONFIG = { strategy: 'fixed' as const };

const NodeInstances = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const {
    config: { theme },
  } = useAppContext();

  const { nodes: nodeInstances, config: headerConfig, filtersConfig, pagination, error, loading, countError } = useAppSelector((state) => state.nodeInstances);
  const userData = useAppSelector((state) => state.auth.userData);
  const { isVisible: isPanelVisible, node_instance_id: currentPanelNodeId } = useAppSelector((state) => state.bottomPanel);
  const { currentPage, totalPages, totalItems, pageSize } = pagination;
  const [showSidebar, setShowSidebar] = useState(false);
  const pageName = 'node-instances';
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<number | null>(null);
  const [skipWorkerDeletion, setSkipWorkerDeletion] = useState(false);
  const [showKillModal, setShowKillModal] = useState(false);
  const [killTargetId, setKillTargetId] = useState<number | null>(null);
  const [unblockTargetId, setUnblockTargetId] = useState<number | null>(null);
  const folders = useAppSelector((state) => state.listOfFolders?.folders ?? []);
  const knownNodeTypes = useAppSelector((state) => state.listOfNodeTypes?.node_types ?? []);
  const nodeTypesStatus = useAppSelector((state) => state.listOfNodeTypes?.status);
  const countRequestRef = useRef<any>(null);

  useEffect(() => {
    dispatch(getFolders());
  }, [dispatch]);

  useEffect(() => {
    if (!nodeTypesStatus) dispatch(getNodeTypes() as any);
  }, [dispatch, nodeTypesStatus]);

  const isTableLoading = loading.fetchNodeInstances;
  const isLoadingCount = loading.fetchNodeInstanceCount;

  const effectiveFiltersConfig = useMemo(() => {
    const fromBackend = filtersConfig || [];
    const keys = new Set(fromBackend.map((c: any) => c.key));
    const folderPathOptions = buildFolderPathOptions(folders, null);
    const extra: {
      key: string;
      label: string;
      type: 'search' | 'dropdown';
      default: string;
      options?: string[];
    }[] = [];
    if (!keys.has('folder')) {
      extra.push({
        key: 'folder',
        label: 'Folder (path)',
        type: 'dropdown',
        default: '',
        options: folderPathOptions.filter((o) => o.value !== '').map((o) => o.value),
      });
    }
    if (!keys.has('zone_name')) {
      extra.push({
        key: 'zone_name',
        label: 'Zone',
        type: 'search',
        default: '',
      });
    }
    const merged = [...extra, ...fromBackend];
    const typeIndex = merged.findIndex((item: any) => item.key === 'type');
    const subTypeIndex = merged.findIndex((item: any) => item.key === 'sub_type');

    if (typeIndex !== -1 && subTypeIndex !== -1 && subTypeIndex !== typeIndex + 1) {
      merged.splice(typeIndex + 1, 0, ...merged.splice(subTypeIndex, 1));
    }

    return merged;
  }, [filtersConfig, folders]);

  const { expandedRows, toggleRow, isExpanded } = useExpandedRows('expanded');

  useEffect(() => {
    const bottomPanelId = searchParams.get('bottom_panel_id');

    if (bottomPanelId) {
      const shouldSyncPanel = currentPanelNodeId !== bottomPanelId || !isPanelVisible;

      if (shouldSyncPanel) {
        dispatch(setNodeInstanceId(bottomPanelId));
        dispatch(setVisibility(true));
      }
    } else {
      if (isPanelVisible || currentPanelNodeId) {
        dispatch(clearPanel());
      }
    }
  }, [searchParams, currentPanelNodeId, isPanelVisible, dispatch]);

  const openPanelWithInstance = (instanceId: number) => {
    const currentParams = new URLSearchParams(searchParams);
    currentParams.set('bottom_panel_id', instanceId.toString());
    navigate(`?${currentParams.toString()}`, { replace: true });
  };

  const openDeleteModal = (id: number) => {
    setDeleteTargetId(id);
    setSkipWorkerDeletion(false);
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTargetId(null);
    setSkipWorkerDeletion(false);
  };

  const handleConfirmDelete = async () => {
    if (deleteTargetId == null) return;
    try {
      await dispatch(
        deleteNodeInstance({
          node_instance_id: deleteTargetId,
          skip_worker_deletion: skipWorkerDeletion,
        })
      ).unwrap();
      triggerActionRefetch();
    } catch (error) {
      console.error('Failed to delete instance', error);
    } finally {
      closeDeleteModal();
    }
  };

  const openKillModal = (id: number) => {
    setKillTargetId(id);
    setShowKillModal(true);
  };

  const closeKillModal = () => {
    setShowKillModal(false);
    setKillTargetId(null);
  };

  const openUnblockDialog = (id: number) => {
    setUnblockTargetId(id);
  };

  const closeUnblockDialog = () => {
    setUnblockTargetId(null);
  };

  const handleConfirmKill = async () => {
    if (killTargetId == null) return;
    try {
      await dispatch(killNodeInstance({ node_instance_id: killTargetId })).unwrap();
      triggerActionRefetch();
    } catch (error) {
      console.error('Failed to kill instance', error);
    } finally {
      closeKillModal();
    }
  };

  const {
    filters,
    setFilterAndSync,
    resetFilters,
    updateSort,
    updateUserSettings,
    updatePage,
    updateLimit,
    limit,
    sortColumn,
    sortOrder,
    getFormattedData,
    generateColumns,
    refresh,
    isSilentRefresh,
    triggerActionRefetch,
    payload,
    isHydrated,
  } = useTableController({
    userData,
    pageName,
    filtersConfig: effectiveFiltersConfig,
    headerConfig,
    fetchData: fetchNodeInstances,
    // Timestamps render reactively in the row's own scheduled_start_timezone
    // (or local, per preference); duration via the duration variant.
    columnFormats: {
      created_at: 'datetime',
      scheduled_start_datetime: 'datetime',
      start_datetime: 'datetime',
      end_datetime: 'datetime',
      original_anchor_datetime: 'datetime',
      duration: 'duration',
    },
    sourceTimezoneKey: 'scheduled_start_timezone',
    defaultHiddenColumns: ['previous_node_instance_id', 'original_anchor_datetime'],
    getFormattedRow: (node) => {
      // The backend renders the ad-hoc schedule into a human-readable
      // schedule_display; the raw schedule JSON stays on the row for anything
      // that needs it.
      const schedule = node.schedule_display || '';

      // Combine status and sub_status for display
      // Console substatus: display "waiting" because the parent is waiting on operator action
      // (actual DB status stays 'started' to preserve flow engine logic)
      const effectiveStatus = node.sub_status === 'Console' ? 'Waiting' : node.status;
      const displayStatus = node.sub_status ? `${effectiveStatus} (${node.sub_status})` : node.status;
      const isDeprecated = isNodeTypeDeprecated(node.type, knownNodeTypes);

      const baseType = node.type === 'shell' ? (node.sub_type ? `Shell (${node.sub_type})` : 'Shell') : node.type;
      const displayType = isDeprecated ? `${node.type} (deprecated)` : baseType;

      return {
        ...node,
        type: displayType,
        isDeprecated,
        status: displayStatus,
        originalStatus: node.status,
        schedule,
      };
    },
  });

  // Payload for count: filters + sort + limit, page fixed to 1 (count is independent of page)
  const countPayload = useMemo(() => ({ ...filters, sortColumn, sortOrder, page: 1, limit }), [filters, sortColumn, sortOrder, limit]);

  // Fetch count only when filters/sort/limit change, not on page change.
  // Use filtersConfig?.length (not filtersConfig) so we don't refetch when the list response
  // assigns a new filtersConfig reference on page change.
  const filtersConfigLength = filtersConfig?.length ?? 0;
  useEffect(() => {
    if (!isHydrated || filtersConfigLength === 0) {
      return;
    }

    const previousRequest = countRequestRef.current;
    if (previousRequest) {
      if (typeof previousRequest.abort === 'function') {
        previousRequest.abort();
      }
      countRequestRef.current = null;
    }

    const countThunk = dispatch(fetchNodeInstanceCount(countPayload) as any);
    countRequestRef.current = countThunk;

    return () => {
      if (countRequestRef.current) {
        if (typeof countRequestRef.current.abort === 'function') {
          countRequestRef.current.abort();
        }
        countRequestRef.current = null;
      }
    };
  }, [dispatch, countPayload, isHydrated, filtersConfigLength]);

  const actionsColumn = {
    id: 'actions',
    header: 'Actions',
    cell: ({ row }) => {
      const nodeId = row.original.node_instance_id;
      const effectiveStatus = resolveEffectiveStatusForActions(row.original.originalStatus, row.original.sub_status);

      const isDeprecated = !!row.original.isDeprecated;
      const actions = isDeprecated ? (canDelete.includes(effectiveStatus) ? ['Delete'] : []) : ['Resubmit', 'Show Instance'];

      if (!isDeprecated) {
        if (canCancel.includes(effectiveStatus) && !row.original.parent_node_instance_id) actions.push('Cancel');
        if (canRelease.includes(effectiveStatus)) actions.push('Release');
        if (canHold.includes(effectiveStatus)) actions.push('Hold');
        if (canDisable.includes(effectiveStatus) && row.original.parent_node_instance_id) actions.push('Disable');
        if (canRevertDisable.includes(effectiveStatus)) actions.push('Revert to Scheduled');
        if (canKill.includes(effectiveStatus)) actions.push('Kill');
        if (canDelete.includes(effectiveStatus)) actions.push('Delete');
        if (canUpdate.includes(effectiveStatus) && !row.original.parent_node_instance_id) actions.push('Update');
        if (canOpenUnblockNodeInstanceDialog(row.original)) actions.push('Unblock node instance...');
        if (row.original.submit_and_forget === true && effectiveStatus === 'completed' && row.original.spawned_node_instance_id) {
          actions.push('Check reference instance');
        }
      }

      return (
        <Dropdown>
          <Dropdown.Toggle variant="phoenix-secondary">
            <FontAwesomeIcon icon={faEllipsis} className="fs-10" />
          </Dropdown.Toggle>
          {/* renderOnMount: with strategy:'fixed', lazily mounting the menu on first open races
              Popper's initial position computation and the first click can fail to show the menu;
              mounting it upfront (hidden) makes the first click reliable. */}
          <Dropdown.Menu renderOnMount popperConfig={ACTIONS_DROPDOWN_POPPER_CONFIG}>
            {actions.map((action) => (
              <Dropdown.Item
                key={action}
                onClick={() => {
                  if (action === 'Cancel') {
                    dispatch(cancelNodeInstance({ node_instance_id: nodeId }));
                    triggerActionRefetch();
                  } else if (action === 'Hold') {
                    dispatch(holdNodeInstance({ node_instance_id: nodeId }));
                    triggerActionRefetch();
                  } else if (action === 'Release') {
                    dispatch(releaseNodeInstance({ node_instance_id: nodeId }));
                    triggerActionRefetch();
                  } else if (action === 'Disable') {
                    dispatch(disableNodeInstance({ node_instance_id: nodeId }));
                    triggerActionRefetch();
                  } else if (action === 'Revert to Scheduled') {
                    dispatch(revertDisableNodeInstance({ node_instance_id: nodeId }));
                    triggerActionRefetch();
                  } else if (action === 'Kill') {
                    openKillModal(nodeId);
                  } else if (action === 'Unblock node instance...') {
                    openUnblockDialog(nodeId);
                  } else if (action === 'Resubmit') {
                    const maxWidth = 1200;
                    const width = Math.min(maxWidth, window.innerWidth);
                    const height = 800;
                    const left = window.screenX + window.innerWidth / 2 - width / 2;
                    const top = window.screenY + window.innerHeight / 2 - height / 2;
                    window.open(
                      `/node_resubmit/${encodeURIComponent(row.original.zone_name)}/${encodeURIComponent(row.original.name)}/${row.original.node_instance_id}`,
                      '_blank',
                      `noopener, noreferrer, width=${width}, height=${height}, top=${top}, left=${left}`
                    );
                  } else if (action === 'Show Instance') {
                    window.open(`/instance_viewer/${nodeId}`, '_blank');
                  } else if (action === 'Check reference instance') {
                    const spawnedId = row.original.spawned_node_instance_id;
                    window.open(`/instance_viewer/${spawnedId}`, '_blank');
                  } else if (action === 'Delete') {
                    openDeleteModal(nodeId);
                  } else if (action === 'Update') {
                    const maxWidth = 1200;
                    const width = Math.min(maxWidth, window.innerWidth);
                    const height = 800;
                    const left = window.screenX + window.innerWidth / 2 - width / 2;
                    const top = window.screenY + window.innerHeight / 2 - height / 2;
                    window.open(
                      `/node_update_instance/${encodeURIComponent(row.original.zone_name)}/${encodeURIComponent(row.original.name)}/${row.original.node_instance_id}`,
                      '_blank',
                      `noopener, noreferrer, width=${width}, height=${height}, top=${top}, left=${left}`
                    );
                  }
                }}
              >
                {action}
              </Dropdown.Item>
            ))}
          </Dropdown.Menu>
        </Dropdown>
      );
    },
  };

  const expandColumn = {
    id: 'expand',
    header: '',
    cell: ({ row }) => {
      const nodeId = typeof row.original.node_instance_id === 'string' ? parseInt(row.original.node_instance_id, 10) : row.original.node_instance_id;

      return (
        <ExpandableRow
          row={row}
          theme={theme}
          showFlowInstance={(r) => {
            openPanelWithInstance(r.original.node_instance_id);
          }}
          isExpanded={isExpanded(nodeId)}
          onToggle={() => toggleRow(nodeId)}
          trailing={row.original.has_note ? <NoteIndicator /> : null}
        />
      );
    },
  };

  const baseColumns = generateColumns(
    (key, order) => {
      updateSort(key, order);
      updateUserSettings(key, order);
    },
    [expandColumn]
  );

  const columns = [
    ...baseColumns.map((col: any) => {
      if (col.accessorKey === 'name') {
        return {
          ...col,
          cell: ({ row, getValue }: any) => {
            const name = getValue();
            return (
              <NodeEditorLink nodeDefinitionId={row.original.node_definition_id} nodeName={name} className="fw-medium">
                {name}
              </NodeEditorLink>
            );
          },
        };
      }
      if (col.accessorKey === 'node_instance_id') {
        return {
          ...col,
          cell: ({ row, getValue }: any) => {
            const id = getValue();
            const { originating_node_instance_id, originating_node_definition_name, originating_flow_instance_id, originating_flow_definition_name } =
              row.original;

            return (
              <div>
                <span>{id}</span>
                {originating_node_instance_id && (
                  <div>
                    <OriginatingFlowBadge
                      originatingNodeInstanceId={originating_node_instance_id}
                      originatingNodeDefinitionName={originating_node_definition_name}
                      originatingFlowInstanceId={originating_flow_instance_id}
                      originatingFlowDefinitionName={originating_flow_definition_name}
                      onOpen={openPanelWithInstance}
                    />
                  </div>
                )}
              </div>
            );
          },
        };
      }

      return col;
    }),
    actionsColumn,
  ];

  const formattedData = useMemo(() => getFormattedData(nodeInstances), [nodeInstances]);

  const table = useAdvanceTable({
    data: formattedData,
    columns,
    pageSize: limit,
    pagination: true,
    sortable: true,
    selection: true,
    enableRowSelection: (row) => {
      const status = row.original.status?.toLowerCase();
      return canDelete.includes(status);
    },
    getRowId: (row) => String(row.node_instance_id),
  });

  useEffect(() => {
    if (expandedRows.size === 0 || !nodeInstances?.length) return;

    const availableIds = nodeInstances.map((n) => {
      const id = n.node_instance_id;
      return typeof id === 'string' ? parseInt(id, 10) : id;
    });

    expandedRows.forEach((nodeId) => {
      if (!availableIds.includes(nodeId)) return;

      const node = nodeInstances.find((n) => {
        const nId = typeof n.node_instance_id === 'string' ? parseInt(n.node_instance_id, 10) : n.node_instance_id;
        return nId === nodeId;
      });

      if (!node) return;

      const hasDetails = node.child_nodes && node.child_nodes.length > 0 && node.child_nodes.some((child: any) => child.status !== undefined);

      if (!hasDetails) {
        dispatch(fetchNodeInstanceById(nodeId));
      }
    });
  }, [expandedRows, nodeInstances, dispatch]);

  // Refresh table when an operator message is responded to from the instance viewer
  useEffect(() => {
    const handleOperatorMessageResponded = () => triggerActionRefetch();
    window.addEventListener('operator-message-responded', handleOperatorMessageResponded);
    return () => {
      window.removeEventListener('operator-message-responded', handleOperatorMessageResponded);
    };
  }, [triggerActionRefetch]);

  // `has_note` is fetched with the row, and the Notes tab in this page's own
  // bottom panel writes notes without touching redux — so the indicator would
  // contradict the note the user just added. Polling does not save it either:
  // the panel sets data-polling-lock, which suspends the table's poll for as
  // long as it is open.
  useEffect(() => {
    const handleNotesChanged = () => triggerActionRefetch();
    window.addEventListener(NODE_INSTANCE_NOTES_CHANGED, handleNotesChanged);
    return () => {
      window.removeEventListener(NODE_INSTANCE_NOTES_CHANGED, handleNotesChanged);
    };
  }, [triggerActionRefetch]);

  const selectedInstancesToDelete = table.getSelectedRowModel().rows.map((row) => row.original);

  const handleBulkDelete = () => {
    setDeleteTargetId(null);
    setSkipWorkerDeletion(false);
    setShowDeleteModal(true);
  };

  const handleConfirmBulkDelete = async () => {
    const selectedIds = selectedInstancesToDelete.map((instance) => instance.node_instance_id);

    if (selectedIds.length === 0) return;

    try {
      await dispatch(
        deleteMultipleNodeInstances({
          node_instance_ids: selectedIds,
          skip_worker_deletion: skipWorkerDeletion,
        })
      ).unwrap();
      table.resetRowSelection();
    } catch (error) {
      console.error('Failed to delete instances', error);
    } finally {
      closeDeleteModal();
    }
  };

  const handleModalConfirm = () => {
    if (deleteTargetId === null) {
      handleConfirmBulkDelete();
    } else {
      handleConfirmDelete();
    }
  };

  return (
    <>
      <TableViewLayout
        filtersConfig={effectiveFiltersConfig}
        filters={filters}
        pageName={pageName}
        columns={headerConfig.map((c: any) => ({
          key: c.header_key,
          label: c.label,
        }))}
        pageSize={limit}
        error={error}
        onFilterChange={setFilterAndSync}
        onResetFilters={resetFilters}
        onPageSizeChange={updateLimit}
        currentPage={currentPage}
        totalPages={totalPages}
        totalItems={totalItems}
        onPageChange={updatePage}
        table={table}
        showSidebar={showSidebar}
        setShowSidebar={setShowSidebar}
        onRefresh={refresh}
        topScrollBar
        isLoading={isTableLoading}
        isSilentRefresh={isSilentRefresh}
        isLoadingCount={isLoadingCount}
        countUnavailable={countError === 'timeout'}
        countErrorMessage={countError === 'timeout' ? 'Total count is unavailable; try narrowing filters.' : undefined}
        toolbarButtons={
          <Button onClick={handleBulkDelete} disabled={selectedInstancesToDelete.length === 0}>
            Delete Selected
          </Button>
        }
        bottomPanel={<ResizableBottomPanel />}
      />

      <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm deletion</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {deleteTargetId === null ? (
            <p>
              {selectedInstancesToDelete.length === 1
                ? 'Are you sure you want to delete this node instance?'
                : `Are you sure you want to delete ${selectedInstancesToDelete.length} node instances?`}
            </p>
          ) : (
            <p>Are you sure you want to delete this node instance?</p>
          )}

          <Form.Check
            type="checkbox"
            id="skip-worker-deletion"
            label="Skip instance deletion on worker side"
            checked={skipWorkerDeletion}
            onChange={(e) => setSkipWorkerDeletion(e.target.checked)}
          />
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeDeleteModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleModalConfirm}>
            Confirm Delete
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showKillModal} onHide={closeKillModal} centered>
        <Modal.Header closeButton>
          <Modal.Title>Confirm kill</Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <p>Are you sure you want to kill this node instance?</p>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={closeKillModal}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleConfirmKill}>
            Confirm Kill
          </Button>
        </Modal.Footer>
      </Modal>

      <UnblockNodeInstanceDialog
        show={unblockTargetId !== null}
        nodeInstanceId={unblockTargetId}
        onHide={closeUnblockDialog}
        onChanged={triggerActionRefetch}
      />
    </>
  );
};

export default NodeInstances;
